import React, { useState, useEffect } from "react";
import { 
  Avatar,
  Box,
  Card,
  CardContent,
  Typography, 
  Grid, 
  IconButton, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Link,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  Collapse,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SendIcon from "@mui/icons-material/Send";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DescriptionIcon from "@mui/icons-material/Description";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useSelector } from "react-redux";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import { convertMessageToHtml } from "../../utilities/helpers";
import { MESSAGE_STYLES } from "../../utilities/constants";

// Counted from the observations list, the same source the extraction details show.
const countObservations = (extraction) =>
  Array.isArray(extraction.observations) ? extraction.observations.length : (extraction.observation_count || 0);

const Form483Extractions = () => {
  // Tab State
  const [activeTab, setActiveTab] = useState(0);
  
  // State for extractions (kept for backward compatibility with details dialog)
  const [extractions, setExtractions] = useState([]);
  
  // State for investigators from new paginated endpoint
  const [investigators, setInvestigators] = useState([]);
  const [investigatorPage, setInvestigatorPage] = useState(1);
  const [investigatorTotalPages, setInvestigatorTotalPages] = useState(1);
  const [investigatorTotalCount, setInvestigatorTotalCount] = useState(0);
  const [investigatorSearchTerm, setInvestigatorSearchTerm] = useState("");
  const [isLoadingInvestigators, setIsLoadingInvestigators] = useState(false);
  
  // State for selected investigator
  const [selectedInvestigatorData, setSelectedInvestigatorData] = useState(null);
  const [isLoadingInvestigatorDetails, setIsLoadingInvestigatorDetails] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // UI State
  const [isLoadingExtractions, setIsLoadingExtractions] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedExtraction, setSelectedExtraction] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openInvestigatorPicker, setOpenInvestigatorPicker] = useState(false);
  
  // AI Q&A State
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiQuestion, setAiQuestion] = useState("");
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  
  // Extractions List State (Tab 2)
  const [extractionsList, setExtractionsList] = useState([]);
  const [extractionsPage, setExtractionsPage] = useState(1);
  const [extractionsTotalPages, setExtractionsTotalPages] = useState(1);
  const [extractionsTotalCount, setExtractionsTotalCount] = useState(0);
  const [isLoadingExtractionsList, setIsLoadingExtractionsList] = useState(false);
  const [countryFilter, setCountryFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  const idToken = useSelector((state) => state.token);

  const requestHeader = {
    headers: {
      Authorization: idToken,
    },
  };

  useEffect(() => {
    fetchInvestigators(investigatorPage === 1 && !investigatorSearchTerm);
    // eslint-disable-next-line
  }, [investigatorPage, investigatorSearchTerm]);

  // State for all extractions (unfiltered) for client-side filtering
  const [allExtractionsList, setAllExtractionsList] = useState([]);

  // Fetch extractions list when tab 2 is active
  useEffect(() => {
    if (activeTab === 1 && allExtractionsList.length === 0) {
      fetchExtractionsList();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  // Apply client-side filters when filters or page change
  useEffect(() => {
    if (activeTab === 1 && allExtractionsList.length > 0) {
      applyFilters();
    }
    // eslint-disable-next-line
  }, [countryFilter, yearFilter, extractionsPage, allExtractionsList]);

  // Fetch all extractions for Tab 2 (client-side filtering)
  const fetchExtractionsList = async () => {
    try {
      setIsLoadingExtractionsList(true);
      setErrorMessage(null);
      
      // Fetch all extractions for client-side filtering
      const response = await ieqipCoreServiceApi.get(
        `/api/form483/extractions?page=1&page_size=100`, 
        requestHeader
      );
      
      const data = response.data;
      
      // Handle both array and paginated response formats
      let extractionItems = [];
      if (Array.isArray(data)) {
        extractionItems = data;
      } else {
        extractionItems = data.extractions || data.items || data || [];
      }
      
      setAllExtractionsList(extractionItems);
      
      // Extract unique countries and years for filters
      const countries = [...new Set(extractionItems.map(e => e.firm_country).filter(c => c))].sort();
      const years = [...new Set(extractionItems.map(e => {
        if (e.date_issued) {
          return new Date(e.date_issued).getFullYear().toString();
        }
        return null;
      }).filter(y => y))].sort().reverse();
      
      setAvailableCountries(countries);
      setAvailableYears(years);
      
      // Apply initial filters
      applyFiltersToData(extractionItems);
      
    } catch (error) {
      console.error("Failed to fetch extractions list:", error);
      setErrorMessage(`Failed to fetch extractions: ${error.message}`);
    } finally {
      setIsLoadingExtractionsList(false);
    }
  };

  // Apply filters to the data
  const applyFilters = () => {
    applyFiltersToData(allExtractionsList);
  };

  const applyFiltersToData = (data) => {
    let filtered = [...data];
    
    // Apply country filter
    if (countryFilter) {
      filtered = filtered.filter(e => e.firm_country === countryFilter);
    }
    
    // Apply year filter
    if (yearFilter) {
      filtered = filtered.filter(e => {
        if (e.date_issued) {
          return new Date(e.date_issued).getFullYear().toString() === yearFilter;
        }
        return false;
      });
    }
    
    // Calculate pagination
    const pageSize = 20;
    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / pageSize) || 1;
    const startIndex = (extractionsPage - 1) * pageSize;
    const paginatedData = filtered.slice(startIndex, startIndex + pageSize);
    
    setExtractionsList(paginatedData);
    setExtractionsTotalPages(totalPages);
    setExtractionsTotalCount(totalCount);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExtractionsPageChange = (event, value) => {
    setExtractionsPage(value);
  };

  const handleCountryFilterChange = (event) => {
    setCountryFilter(event.target.value);
    setExtractionsPage(1);
  };

  const handleYearFilterChange = (event) => {
    setYearFilter(event.target.value);
    setExtractionsPage(1);
  };

  const handleClearFilters = () => {
    setCountryFilter("");
    setYearFilter("");
    setExtractionsPage(1);
  };

  const handleRefreshExtractionsList = () => {
    setAllExtractionsList([]);
    setAvailableCountries([]);
    setAvailableYears([]);
    fetchExtractionsList();
  };

  // Fetch investigators from new paginated endpoint
  const fetchInvestigators = async (autoSelectFirst = false) => {
    try {
      setIsLoadingInvestigators(true);
      setErrorMessage(null);
      
      const params = new URLSearchParams({
        page: investigatorPage.toString(),
        page_size: '20'
      });
      
      if (investigatorSearchTerm) {
        params.append('search', investigatorSearchTerm);
      }

      const response = await ieqipCoreServiceApi.get(
        `/api/form483/investigator-directory?${params.toString()}`,
        requestHeader
      );

      const data = response.data;

      setInvestigators(data.investigators || []);
      setInvestigatorTotalPages(data.total_pages || 1);
      setInvestigatorTotalCount(data.total_count || 0);
      
      // Auto-select first investigator only on initial load
      if (autoSelectFirst && !selectedInvestigatorData && data.investigators && data.investigators.length > 0) {
        await handleSelectInvestigator(data.investigators[0]);
      }

    } catch (error) {
      console.error("Failed to fetch investigators:", error);
      setErrorMessage(`Failed to fetch investigators: ${error.message}`);
    } finally {
      setIsLoadingInvestigators(false);
      if (autoSelectFirst) setIsInitialLoading(false);
    }
  };

  // Fetch every page of inspection rows for an investigator name
  const fetchAllInvestigatorRows = async (investigatorKey) => {
    const rows = [];
    let page = 1;
    let totalCount = 0;
    do {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '100',
        investigator_key: investigatorKey
      });
      const response = await ieqipCoreServiceApi.get(
        `/api/form483/investigators?${params.toString()}`,
        requestHeader
      );
      const data = response.data;
      totalCount = data.total_count || 0;
      rows.push(...(data.investigators || []));
      if (!data.investigators || data.investigators.length === 0) break;
      page += 1;
    } while (rows.length < totalCount);
    return rows;
  };

  // Fetch detailed information for a specific investigator
  const handleSelectInvestigator = async (investigator) => {
    setOpenInvestigatorPicker(false);
    try {
      setIsLoadingInvestigatorDetails(true);
      setErrorMessage(null);

      // First, fetch all extractions to have complete data including observations
      const extractionsData = await fetchExtractions();
      
      const investigatorRows = await fetchAllInvestigatorRows(investigator.investigator_key);

      // One entry per 483, even when the investigator is listed twice on the same form
      // or under a name variant (the rows come from every variant of the same name).
      const allInspections = [];
      const seenExtractionIds = new Set();
      investigatorRows.forEach(inv => {
        if (!inv.extraction || seenExtractionIds.has(inv.extraction.id)) return;
        seenExtractionIds.add(inv.extraction.id);
        // Find the full extraction with observations from extractionsData
        const fullExtraction = extractionsData.find(e => e.id === inv.extraction.id);
        allInspections.push({
          ...(fullExtraction || inv.extraction),
          investigator_order: inv.investigator_order
        });
      });
      
      // Build investigator data structure
      const investigatorData = {
        name: investigator.investigator_name,
        title: investigator.investigator_title,
        designation: investigator.investigator_designation,
        inspections: allInspections
      };
      
      setSelectedInvestigatorData(investigatorData);

    } catch (error) {
      console.error("Failed to fetch investigator details:", error);
      setErrorMessage(`Failed to fetch investigator details: ${error.message}`);
    } finally {
      setIsLoadingInvestigatorDetails(false);
    }
  };

  // Fetch every extraction, not just the first page, so each investigator inspection gets its full record
  const fetchExtractions = async () => {
    try {
      setIsLoadingExtractions(true);
      const data = [];
      let page = 1;
      let batch = [];
      do {
        const response = await ieqipCoreServiceApi.get(
          `/api/form483/extractions?page=${page}&page_size=100`,
          requestHeader
        );
        batch = response.data || [];
        data.push(...batch);
        page += 1;
      } while (batch.length === 100);
      setExtractions(data);
      return data;
    } catch (error) {
      console.error("Failed to fetch extractions:", error);
      setErrorMessage(`Failed to fetch Form 483 extractions: ${error.message}`);
      return [];
    } finally {
      setIsLoadingExtractions(false);
    }
  };

  const handleViewDetails = async (inspection) => {
    try {
      setIsLoadingExtractions(true);
      setErrorMessage(null);
      
      // Fetch all extractions if not already loaded
      if (extractions.length === 0) {
        await fetchExtractions();
      }
      
      // Find the full extraction data with observations
      const fullExtraction = extractions.find(e => e.id === inspection.id);
      
      if (fullExtraction) {
        setSelectedExtraction(fullExtraction);
      } else {
        // If not found in local data, try to fetch from API
        const response = await ieqipCoreServiceApi.get(
          `/api/form483/extractions?page=1&page_size=100`, 
          requestHeader
        );
        const data = response.data || [];
        const extraction = data.find(e => e.id === inspection.id);
        setSelectedExtraction(extraction || inspection);
      }
      
      setOpenDialog(true);
    } catch (error) {
      console.error("Failed to fetch extraction details:", error);
      setErrorMessage(`Failed to fetch extraction details: ${error.message}`);
      // Still open dialog with available data
      setSelectedExtraction(inspection);
      setOpenDialog(true);
    } finally {
      setIsLoadingExtractions(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedExtraction(null);
  };

  const getInitials = (name) =>
    name.split(" ").filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase();

  const formatInspectionDate = (isoDate) => {
    if (!isoDate) return "N/A";
    return new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const handleOpenInvestigatorPicker = () => {
    setOpenInvestigatorPicker(true);
  };

  const handleCloseInvestigatorPicker = () => {
    setOpenInvestigatorPicker(false);
  };

  const handleInvestigatorPageChange = (event, value) => {
    setInvestigatorPage(value);
  };

  const handleSearchChange = (event) => {
    setInvestigatorSearchTerm(event.target.value);
    setInvestigatorPage(1); // Reset to first page on search
  };

  const handleAskAI = async () => {
    if (!aiQuestion.trim()) return;

    try {
      setIsLoadingAI(true);
      setErrorMessage(null);

      const response = await ieqipCoreServiceApi.post(
        '/api/form483/search',
        { question: aiQuestion },
        requestHeader
      );

      const data = response.data;
      
      // Add to history
      setAiHistory(prev => [
        ...prev,
        {
          question: aiQuestion,
          answer: data.answer,
          timestamp: new Date().toISOString()
        }
      ]);

      setAiQuestion(""); // Clear input after successful query
    } catch (error) {
      console.error("Failed to get AI answer:", error);
      setErrorMessage(`Failed to get AI answer: ${error.message}`);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleAIKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleAskAI();
    }
  };

  const getExtractionStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  // Calculate investigator statistics
  const getInvestigatorStats = () => {
    if (!selectedInvestigatorData) return { totalInspections: 0, total483sIssued: 0, warningLettersIssued: 0, totalObservations: 0 };

    const inspections = selectedInvestigatorData.inspections || [];

    // Sum up warning letters count from all inspections
    const warningLettersCount = inspections.reduce((sum, inspection) => {
      return sum + (inspection.warning_letters_count || 0);
    }, 0);

    const observationsCount = inspections.reduce((sum, inspection) => sum + countObservations(inspection), 0);

    return {
      totalInspections: inspections.length,
      total483sIssued: inspections.filter(i => i.extraction_status === "completed").length,
      warningLettersIssued: warningLettersCount,
      totalObservations: observationsCount,
    };
  };

  // Get inspection details
  const getInspectionDetails = () => {
    if (!selectedInvestigatorData) return { longest: 0, shortest: 0, average: 0 };

    const inspections = selectedInvestigatorData.inspections || [];
    const durations = inspections.map(i => {
      if (i.inspection_start_date && i.inspection_end_date) {
        const start = new Date(i.inspection_start_date);
        const end = new Date(i.inspection_end_date);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
      }
      return 0;
    }).filter(d => d > 0);
    
    if (durations.length === 0) return { longest: 0, shortest: 0, average: 0 };

    return {
      longest: Math.max(...durations),
      shortest: Math.min(...durations),
      average: Math.round((durations.reduce((sum, d) => sum + d, 0) / durations.length) * 10) / 10
    };
  };

  // Get co-inspectors for selected investigator
  const getCoInspectors = () => {
    if (!selectedInvestigatorData) return [];

    const coInspectorMap = new Map();
    
    // We need to fetch full extraction data to get co-inspectors
    selectedInvestigatorData.inspections.forEach(inspection => {
      const fullExtraction = extractions.find(e => e.id === inspection.id);
      if (fullExtraction && fullExtraction.investigators) {
        fullExtraction.investigators.forEach(inv => {
          if (inv.investigator_name !== selectedInvestigatorData.name) {
            const key = inv.investigator_name;
            if (!coInspectorMap.has(key)) {
              coInspectorMap.set(key, {
                name: inv.investigator_name,
                inspections: 0,
                lastInspection: null
              });
            }
            
            const coInspector = coInspectorMap.get(key);
            coInspector.inspections += 1;
            
            const inspectionDate = new Date(fullExtraction.inspection_end_date);
            if (!coInspector.lastInspection || inspectionDate > new Date(coInspector.lastInspection)) {
              coInspector.lastInspection = fullExtraction.inspection_end_date;
            }
          }
        });
      }
    });

    return Array.from(coInspectorMap.values())
      .sort((a, b) => b.inspections - a.inspections)
      .slice(0, 15); // Limit to top 15 co-inspectors
  };

  const stats = getInvestigatorStats();
  const inspectionDetails = getInspectionDetails();
  const coInspectors = getCoInspectors();

  return (
    <Box m="20px">
      {/* Page Title */}
      <Typography variant="h2" fontWeight="bold" color="#1976d2" mb={2}>
        Form 483 Analysis
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="form 483 tabs">
          <Tab 
            icon={<PersonIcon />} 
            iconPosition="start" 
            label="Investigator Report" 
            id="tab-0"
            aria-controls="tabpanel-0"
          />
          <Tab 
            icon={<DescriptionIcon />} 
            iconPosition="start" 
            label="Form 483 Extractions" 
            id="tab-1"
            aria-controls="tabpanel-1"
          />
        </Tabs>
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Tab 0: Investigator Report */}
      <Box role="tabpanel" hidden={activeTab !== 0} id="tabpanel-0" aria-labelledby="tab-0">
        {activeTab === 0 && (
          <>
            {/* Header with Investigator Selection */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box display="flex" alignItems="center" gap={3}>
                <Typography variant="h3" fontWeight="bold" color="#1976d2">
                  FDA Investigator Report
                </Typography>
              </Box>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PersonIcon />}
                  onClick={handleOpenInvestigatorPicker}
                  sx={{ minWidth: 200 }}
                >
                  {selectedInvestigatorData 
                    ? `${selectedInvestigatorData.name}` 
                    : "Select Investigator"}
                </Button>
                <Button
                  variant={showAIChat ? "contained" : "outlined"}
                  color="secondary"
                  startIcon={<SmartToyIcon />}
            onClick={() => setShowAIChat(!showAIChat)}
          >
            AI Q&A
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={fetchInvestigators}
            disabled={isLoadingInvestigators}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* AI Q&A Section */}
      <Collapse in={showAIChat}>
        <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <SmartToyIcon color="secondary" />
            <Typography variant="h5" fontWeight="bold">
              Ask AI About Form 483 Inspections
            </Typography>
            <IconButton size="small" onClick={() => setShowAIChat(false)}>
              <ExpandLessIcon />
            </IconButton>
          </Box>
          
          <Box display="flex" gap={2} mb={2}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask a question... (e.g., Find all inspections for firm PolyPeptide?)"
              value={aiQuestion}
              onChange={(e) => setAiQuestion(e.target.value)}
              onKeyPress={handleAIKeyPress}
              disabled={isLoadingAI}
              sx={{ backgroundColor: 'white' }}
            />
            <Button
              variant="contained"
              color="secondary"
              startIcon={isLoadingAI ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={handleAskAI}
              disabled={!aiQuestion.trim() || isLoadingAI}
              sx={{ minWidth: 100 }}
            >
              Ask
            </Button>
          </Box>

          {/* AI History */}
          {aiHistory.length > 0 && (
            <Box>
              {aiHistory.slice().reverse().map((item, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, backgroundColor: 'white' }}>
                  <Box display="flex" alignItems="start" gap={1} mb={1}>
                    <PersonIcon color="primary" fontSize="small" />
                    <Typography variant="body1" fontWeight="bold">
                      {item.question}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="start" gap={1}>
                    <SmartToyIcon color="secondary" fontSize="small" sx={{ mt: 0.5 }} />
                    <Box
                      sx={{
                        flex: 1,
                        color: "text.primary",
                        "& pre": MESSAGE_STYLES.pre,
                        "& code": MESSAGE_STYLES.code,
                        "& strong": MESSAGE_STYLES.strong,
                        "& em": MESSAGE_STYLES.em,
                        "& h1": MESSAGE_STYLES.h1,
                        "& h2": MESSAGE_STYLES.h2,
                        "& h3": MESSAGE_STYLES.h3,
                        "& h4": MESSAGE_STYLES.h4,
                        "& h5": MESSAGE_STYLES.h5,
                        "& h6": MESSAGE_STYLES.h6,
                        "& ul": MESSAGE_STYLES.ul,
                        "& ol": MESSAGE_STYLES.ol,
                        "& li": MESSAGE_STYLES.li,
                        "& p": MESSAGE_STYLES.p,
                        "& a": MESSAGE_STYLES.a,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word"
                      }}
                      dangerouslySetInnerHTML={{
                        __html: convertMessageToHtml(item.answer)
                      }}
                    />
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>
      </Collapse>

      {(isInitialLoading || isLoadingInvestigatorDetails) && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      )}

      {selectedInvestigatorData && !isInitialLoading && !isLoadingInvestigatorDetails && (
        <>
          {/* Investigator Header Information */}
          <Typography variant="h3" fontWeight="bold" color="#1976d2" mb={3} textAlign="center">
            {selectedInvestigatorData.name} - FDA Investigator Report
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Designation */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <WorkIcon color="primary" />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Designation:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {selectedInvestigatorData.designation || selectedInvestigatorData.title || "CONSUMER SAFETY OFFICER"}
                </Typography>
              </Paper>
            </Grid>

            {/* Duty Station */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LocationOnIcon color="primary" />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Duty Station:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  District Office
                </Typography>
              </Paper>
            </Grid>

            {/* Organizations */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Organizations:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  DHHS FDA ORA OGROP ORA OHAFO OHAFOW
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Overview Statistics */}
          <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
            Overview Statistics
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #5472d3 0%, #3f51b5 100%)', 
                color: 'white',
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h1" fontWeight="bold" mb={1}>
                    {stats.totalInspections}
                  </Typography>
                  <Typography variant="h6" fontWeight="500">
                    Total Inspections
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #5472d3 0%, #3f51b5 100%)', 
                color: 'white',
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h1" fontWeight="bold" mb={1}>
                    {stats.total483sIssued}
                  </Typography>
                  <Typography variant="h6" fontWeight="500">
                    Total 483s Issued
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #5472d3 0%, #3f51b5 100%)', 
                color: 'white',
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h1" fontWeight="bold" mb={1}>
                    {stats.warningLettersIssued}
                  </Typography>
                  <Typography variant="h6" fontWeight="500">
                    Warning Letters Issued
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{
                background: 'linear-gradient(135deg, #5472d3 0%, #3f51b5 100%)',
                color: 'white',
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h1" fontWeight="bold" mb={1}>
                    {stats.totalObservations}
                  </Typography>
                  <Typography variant="h6" fontWeight="500">
                    Total Observations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Inspection Details */}
          <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
            Inspection Details
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Longest Inspection:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {inspectionDetails.longest} days
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Shortest Inspection:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {inspectionDetails.shortest} days
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, border: '2px solid #1976d2', borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Average Length:
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="bold">
                  {inspectionDetails.average} days
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Inspections Table */}
          <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
            {selectedInvestigatorData.name}'s Inspections
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#1976d2' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Company</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>City</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Country</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>FEI Number</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Observations</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>483</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedInvestigatorData.inspections
                  .sort((a, b) => new Date(b.inspection_end_date) - new Date(a.inspection_end_date))
                  .map((inspection, index) => (
                  <TableRow key={index}>
                    <TableCell>{inspection.inspection_end_date}</TableCell>
                    <TableCell>{inspection.firm_name}</TableCell>
                    <TableCell>{inspection.firm_city}</TableCell>
                    <TableCell>{inspection.firm_country}</TableCell>
                    <TableCell>{inspection.fei_number}</TableCell>
                    <TableCell>{countObservations(inspection)}</TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleViewDetails(inspection)}
                        size="small"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                  <TableCell colSpan={5} align="right">
                    Total observations (all {stats.totalInspections} inspections)
                  </TableCell>
                  <TableCell>{stats.totalObservations}</TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>

          {/* Co-Inspectors Section */}
          <Typography variant="h4" fontWeight="bold" color="#1976d2" textAlign="center" mb={3}>
            {selectedInvestigatorData.name} - Co-Inspectors
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#1976d2' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Inspections</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Inspection</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coInspectors.map((coInspector, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Link 
                        component="button" 
                        variant="body1" 
                        color="primary"
                        onClick={() => {
                          // Find the investigator in the list and select them
                          const inv = investigators.find(i => i.investigator_name === coInspector.name);
                          if (inv) {
                            handleSelectInvestigator(inv);
                          }
                        }}
                        sx={{ textDecoration: 'none', fontWeight: 'bold' }}
                      >
                        {coInspector.name}
                      </Link>
                    </TableCell>
                    <TableCell>{coInspector.inspections}</TableCell>
                    <TableCell>{coInspector.lastInspection ? new Date(coInspector.lastInspection).getFullYear() : 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
            </>
          )}
          </>
        )}
      </Box>

      {/* Tab 1: Form 483 Extractions List */}
      <Box role="tabpanel" hidden={activeTab !== 1} id="tabpanel-1" aria-labelledby="tab-1">
        {activeTab === 1 && (
          <>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h3" fontWeight="bold" color="#1976d2">
                Form 483 Extractions
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshExtractionsList}
                disabled={isLoadingExtractionsList}
              >
                Refresh
              </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FilterListIcon color="primary" />
                <Typography variant="h6" color="primary" fontWeight="bold">
                  Filters:
                </Typography>
                
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={countryFilter}
                    label="Country"
                    onChange={handleCountryFilterChange}
                  >
                    <MenuItem value="">All Countries</MenuItem>
                    {availableCountries.map((country) => (
                      <MenuItem key={country} value={country}>{country}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={yearFilter}
                    label="Year"
                    onChange={handleYearFilterChange}
                  >
                    <MenuItem value="">All Years</MenuItem>
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(countryFilter || yearFilter) && (
                  <Button 
                    variant="text" 
                    color="secondary" 
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                )}

                <Box sx={{ flexGrow: 1 }} />
                
                <Typography variant="body2" color="textSecondary">
                  {extractionsTotalCount} total extractions
                </Typography>
              </Box>
            </Paper>

            {/* Loading State */}
            {isLoadingExtractionsList && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </Box>
            )}

            {/* Extractions Table */}
            {!isLoadingExtractionsList && (
              <>
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: '#1976d2' }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Firm Name</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>FEI Number</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Country</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date Issued</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Observations</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {extractionsList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body1" color="textSecondary" py={4}>
                              No extractions found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        extractionsList.map((extraction, index) => (
                          <TableRow key={extraction.id || index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {extraction.firm_name || "N/A"}
                              </Typography>
                            </TableCell>
                            <TableCell>{extraction.fei_number || "N/A"}</TableCell>
                            <TableCell>{extraction.firm_country || "N/A"}</TableCell>
                            <TableCell>{extraction.date_issued || "N/A"}</TableCell>
                            <TableCell>
                              <Chip 
                                label={extraction.observation_count || 0} 
                                size="small" 
                                color="primary" 
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={extraction.extraction_status?.toUpperCase() || "N/A"} 
                                size="small"
                                color={getExtractionStatusColor(extraction.extraction_status)}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton 
                                color="primary" 
                                onClick={() => handleViewDetails(extraction)}
                                size="small"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {extractionsTotalPages > 1 && (
                  <Box display="flex" justifyContent="center" mb={3}>
                    <Pagination
                      count={extractionsTotalPages}
                      page={extractionsPage}
                      onChange={handleExtractionsPageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {/* Investigator Picker Dialog */}
      <Dialog
        open={openInvestigatorPicker}
        onClose={handleCloseInvestigatorPicker}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight="bold">Select an Investigator</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search investigator by name..."
            value={investigatorSearchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Result Count */}
          {!isLoadingInvestigators && investigatorTotalCount > 0 && (
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Showing {investigators.length} of {investigatorTotalCount} investigators 
                {investigatorTotalPages > 1 && ` (Page ${investigatorPage} of ${investigatorTotalPages})`}
              </Typography>
            </Box>
          )}

          {/* Loading State */}
          {isLoadingInvestigators && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          )}

          {/* Investigators List */}
          {!isLoadingInvestigators && investigators.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No investigators found
              </Typography>
            </Box>
          )}

          {!isLoadingInvestigators && investigators.length > 0 && (
            <Box>
              <List disablePadding>
                {investigators.map((investigator, index) => (
                  <React.Fragment key={investigator.investigator_key}>
                    <ListItemButton
                      selected={selectedInvestigatorData?.name === investigator.investigator_name}
                      onClick={() => handleSelectInvestigator(investigator)}
                      sx={{ borderRadius: 1, py: 1.5, px: 2 }}
                    >
                      <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, mr: 2, fontSize: 14 }}>
                        {getInitials(investigator.investigator_name)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {investigator.investigator_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {[investigator.investigator_title, investigator.investigator_designation]
                            .filter(Boolean)
                            .join(" · ") || "Investigator"}
                        </Typography>
                      </Box>
                      <Box textAlign="right" ml={2} flexShrink={0}>
                        <Chip
                          label={`${investigator.inspection_count} inspection${investigator.inspection_count === 1 ? "" : "s"}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                          Latest: {formatInspectionDate(investigator.latest_inspection_date)}
                        </Typography>
                      </Box>
                    </ListItemButton>
                    {index < investigators.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>

              {/* Pagination */}
              {investigatorTotalPages > 1 && (
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  gap={2}
                  mt={3} 
                  pt={2} 
                  borderTop={1} 
                  borderColor="divider"
                >
                  <Typography variant="body2" color="textSecondary">
                    Page {investigatorPage} of {investigatorTotalPages}
                  </Typography>
                  <Pagination
                    count={investigatorTotalPages}
                    page={investigatorPage}
                    onChange={handleInvestigatorPageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {investigatorTotalCount > 0 && `Total: ${investigatorTotalCount} investigators`}
          </Typography>
          <Button onClick={handleCloseInvestigatorPicker} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Form 483 Extraction Details</Typography>
            <Chip
              label={selectedExtraction?.extraction_status?.toUpperCase() || "UNKNOWN"}
              color={getExtractionStatusColor(selectedExtraction?.extraction_status)}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingExtractions && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress />
            </Box>
          )}
          {!isLoadingExtractions && selectedExtraction && (
            <Box>
              {/* Firm Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom>Firm Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Firm Name</Typography>
                    <Typography variant="body1">{selectedExtraction.firm_name || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">FEI Number</Typography>
                    <Typography variant="body1">{selectedExtraction.fei_number || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Address</Typography>
                    <Typography variant="body1">
                      {selectedExtraction.firm_street_address || selectedExtraction.street_address || "N/A"}
                      {selectedExtraction.firm_city || selectedExtraction.city ? `, ${selectedExtraction.firm_city || selectedExtraction.city}` : ""}
                      {selectedExtraction.firm_state || selectedExtraction.state ? `, ${selectedExtraction.firm_state || selectedExtraction.state}` : ""}
                      {selectedExtraction.firm_zip_code || selectedExtraction.zip_code ? ` ${selectedExtraction.firm_zip_code || selectedExtraction.zip_code}` : ""}
                    </Typography>
                    <Typography variant="body1">{selectedExtraction.firm_country || selectedExtraction.country || "N/A"}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Inspection Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom>Inspection Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Inspection Start</Typography>
                    <Typography variant="body1">{selectedExtraction.inspection_start_date || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Inspection End</Typography>
                    <Typography variant="body1">{selectedExtraction.inspection_end_date || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Date Issued</Typography>
                    <Typography variant="body1">{selectedExtraction.date_issued || "N/A"}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* File Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom>File Information</Typography>
                <Typography variant="body2" color="textSecondary">Original Filename</Typography>
                <Typography variant="body1" sx={{ mb: 1, wordBreak: "break-all" }}>
                  {selectedExtraction.original_filename}
                </Typography>
                <Typography variant="body2" color="textSecondary">S3 Filename</Typography>
                <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                  {selectedExtraction.s3_filename}
                </Typography>
              </Paper>

              {/* Observations */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: "background.default" }}>
                <Typography variant="h6" gutterBottom>
                  Observations ({selectedExtraction.observations?.length || 0})
                </Typography>
                {selectedExtraction.observations && selectedExtraction.observations.length > 0 ? (
                  <List>
                    {selectedExtraction.observations.map((observation, index) => (
                      <React.Fragment key={index}>
                        <ListItem alignItems="flex-start" sx={{ flexDirection: "column", alignItems: "flex-start", py: 2 }}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <Chip
                              label={`#${observation.observation_number}`}
                              size="small"
                              color="primary"
                              sx={{ mr: 1 }}
                            />
                            <Typography variant="subtitle2" fontWeight="bold">
                              {observation.title || observation.description || "No Title"}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              mt: 1,
                              color: "text.secondary",
                              "& pre": MESSAGE_STYLES.pre,
                              "& code": MESSAGE_STYLES.code,
                              "& strong": MESSAGE_STYLES.strong,
                              "& em": MESSAGE_STYLES.em,
                              "& h1": MESSAGE_STYLES.h1,
                              "& h2": MESSAGE_STYLES.h2,
                              "& h3": MESSAGE_STYLES.h3,
                              "& h4": MESSAGE_STYLES.h4,
                              "& h5": MESSAGE_STYLES.h5,
                              "& h6": MESSAGE_STYLES.h6,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word"
                            }}
                            dangerouslySetInnerHTML={{
                              __html: convertMessageToHtml(observation.full_text || observation.observation_text || "No observation text available")
                            }}
                          />
                        </ListItem>
                        {index < selectedExtraction.observations.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No observations found
                  </Typography>
                )}
              </Paper>

              {/* Error Message if any */}
              {selectedExtraction.error_message && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Error:</strong> {selectedExtraction.error_message}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Form483Extractions;

